import { useState } from 'react';

import { isNil, notNil } from '@/lib/utils';
import { useWorkspaceContext } from '@/workspace/workspace-context';
import { Account } from '@/workspace/types';
import { Button } from '@/components/button';
import { SingleValueSearchInput } from '@/components/form/search-input';
import { OverlayContextProvider } from '@/components/overlay/overlay-context-provider';

import { Cards, Card } from '../../components/gradient-layout/cards';
import { LoginCards } from '../login/login-cards';
import { OAuthConsentStep } from './oauth-consent';
import { OAuthError } from './oauth-status';

import commonStyles from '../public.module.scss';
import styles from './oauth-login.module.scss';

interface OAuthLoginProps {
  oauthSession: string;
}

export const OAuthLogin = ({ oauthSession }: OAuthLoginProps) => {
  const { user, accounts } = useWorkspaceContext();

  if (isNil(user) || !user.emailVerified) {
    return (
      <div className={commonStyles.layout}>
        <h1>Log in to connect</h1>
        <LoginCards />
      </div>
    );
  }

  return <OAuthAuthorize oauthSession={oauthSession} accounts={accounts} />;
};

interface OAuthAuthorizeProps {
  oauthSession: string;
  accounts: Account[];
}

const OAuthAuthorize = ({ oauthSession, accounts }: OAuthAuthorizeProps) => {
  const singleAccountId = accounts.length === 1 ? accounts.at(0)?.accountId : undefined;
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(singleAccountId);

  if (accounts.length === 0) {
    return (
      <OAuthError>
        Your account isn&apos;t set up yet. Finish onboarding before connecting an application.
      </OAuthError>
    );
  }

  const selectedAccount = accounts.find((account) => account.accountId === selectedAccountId);

  if (selectedAccount === undefined) {
    return <AccountPicker accounts={accounts} onSelect={setSelectedAccountId} />;
  }

  return <OAuthConsentStep oauthSession={oauthSession} account={selectedAccount} />;
};

interface AccountPickerProps {
  accounts: Account[];
  onSelect: (accountId: string) => void;
}

const AccountPicker = ({ accounts, onSelect }: AccountPickerProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);

  const accountOptions = accounts
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((account) => ({ label: account.name, value: account.accountId }));

  return (
    <div className={commonStyles.layout}>
      <h1>Choose an account to connect</h1>
      <Cards>
        <Card className={styles.accountPicker}>
          <OverlayContextProvider>
            <SingleValueSearchInput
              options={accountOptions}
              value={selectedAccountId}
              onChange={(option) => setSelectedAccountId(option.value)}
              placeholder="Search for an account..."
              size="large"
              autoFocus
            />
          </OverlayContextProvider>
          <Button
            className={styles.connectButton}
            size="large"
            disabled={isNil(selectedAccountId)}
            onClick={() => {
              if (notNil(selectedAccountId)) {
                onSelect(selectedAccountId);
              }
            }}>
            Continue
          </Button>
        </Card>
      </Cards>
    </div>
  );
};
